// src/TeamChatWidget.js
// Entry point using Vue's native Shadow DOM support NEW

import { defineCustomElement } from "vue";
import ChatWidget from "./components/ChatWidget.vue";

// Convert the Vue component to a custom element with Shadow DOM
const ChatWidgetElement = defineCustomElement(ChatWidget);

// Register the custom element
customElements.define("team-chat-widget", ChatWidgetElement);

// Store references
let widgetInstance = null;
let currentOptions = null;

// Create the global TeamChatWidget object
const TeamChatWidget = {
  /**
   * Default configuration
   */
  defaults: {
    wsUrl: "wss://office-chat.bestshop.ge/ws",
    cssHref: "", // CSS is now bundled in the component
    maxImageSize: 500000,
    maxMessageLength: 1000000,
    compressionQuality: 0.8,
  },

  /**
   * Initialize the widget with configuration
   * @param {Object} options - Configuration options
   */
  async init(options = {}) {
    // Clean up any existing instance
    if (widgetInstance) {
      this.destroy();
    }

    // Merge defaults with provided options
    const finalOptions = {
      ...this.defaults,
      ...options,
    };

    // Validate required fields
    if (
      !finalOptions.apiKey ||
      !finalOptions.userName ||
      !finalOptions.userSurname
    ) {
      console.error(
        "TeamChatWidget: Missing required fields (apiKey, userName, userSurname)"
      );
      return;
    }

    // Generate userId if not provided
    if (!finalOptions.userId) {
      finalOptions.userId = `${finalOptions.userName}_${
        finalOptions.userSurname
      }_${Date.now()}`;
    }

    // Store options
    currentOptions = finalOptions;

    // Create the custom element instance
    widgetInstance = document.createElement("team-chat-widget");

    // Pass options as props
    Object.keys(finalOptions).forEach((key) => {
      widgetInstance[key] = finalOptions[key];
    });

    // Add to DOM
    document.body.appendChild(widgetInstance);

    console.log("TeamChatWidget: Initialized with configuration", {
      ...finalOptions,
      apiKey: "***", // Hide API key in logs
    });
  },

  /**
   * Toggle the chat overlay visibility
   */
  toggle() {
    if (widgetInstance?.toggle) {
      widgetInstance.toggle();
    } else {
      console.warn("TeamChatWidget: Not initialized. Call init() first.");
    }
  },

  /**
   * Open the chat overlay
   */
  open() {
    if (widgetInstance?.open) {
      widgetInstance.open();
    } else {
      console.warn("TeamChatWidget: Not initialized. Call init() first.");
    }
  },

  /**
   * Close the chat overlay
   */
  close() {
    if (widgetInstance?.close) {
      widgetInstance.close();
    } else {
      console.warn("TeamChatWidget: Not initialized. Call init() first.");
    }
  },

  /**
   * Send a message programmatically
   */
  sendMessage(message, type = "text") {
    if (widgetInstance?.sendMessage) {
      return widgetInstance.sendMessage(message, type);
    } else {
      console.warn("TeamChatWidget: Not initialized. Call init() first.");
      return false;
    }
  },

  /**
   * Get current connection type
   */
  getConnectionType() {
    if (widgetInstance?.getConnectionType) {
      return widgetInstance.getConnectionType();
    } else {
      console.warn("TeamChatWidget: Not initialized. Call init() first.");
      return "NOT_INITIALIZED";
    }
  },

  /**
   * Destroy the widget and clean up
   */
  destroy() {
    if (widgetInstance) {
      widgetInstance.remove();
      widgetInstance = null;
    }
    currentOptions = null;
    console.log("TeamChatWidget: Destroyed");
  },
};

// Expose globally
window.TeamChatWidget = TeamChatWidget;

export default TeamChatWidget;
